<script setup>
import { computed, ref } from "vue";
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from "radix-vue";
import { X } from "lucide-vue-next";
import { sheetVariants } from ".";
import { cn } from "@/lib/utils";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  class: { type: null, required: false },
  forceMount: { type: Boolean, required: false },
  trapFocus: { type: Boolean, required: false },
  disableOutsidePointerEvents: { type: Boolean, required: false },
  asChild: { type: Boolean, required: false },
  as: { type: null, required: false },
  varsMenu: { type: Object, required: false },
  readonly: { type: Boolean, required: false },
  disableVars: { type: Boolean, required: false },
});

const emits = defineEmits([
  "escapeKeyDown",
  "pointerDownOutside",
  "focusOutside",
  "interactOutside",
  "openAutoFocus",
  "closeAutoFocus",
  "close"
]);

const delegatedProps = computed(() => {
  const { class: _, side, ...delegated } = props;

  return delegated;
});

const varsMenu = ref(props.varsMenu)

const currPage = ref(varsMenu.value)

const history = ref([])

const navigateToSubmenu = (page) => {
	if (page.submenu?.items?.length > 0) {
		history.value.push(currPage.value)
		currPage.value = page.submenu
	}
}

const goBack = () => {
	if (history.value.length > 0) {
		currPage.value = history.value.pop()
	}
}

const allowDrop = (ev) => {
	ev.preventDefault()
}

const drag = (ev, data) => {
	ev.dataTransfer.setData('text', data)
}

const drop = (ev) => {
	ev.preventDefault()
	const data = ev.dataTransfer.getData('text')
}

const touchStart = (ev, data) => {
  ev.dataTransfer = {
    setData: (type, val) => {
      ev.target.dataset[type] = val;
    }
  };
  drag(ev, data);
}

const touchEnd = (ev) => {
  ev.preventDefault();
  const dropEvent = new Event('drop', { bubbles: true });
  ev.target.dispatchEvent(dropEvent);
}

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const nodePopout = ref(false);
const toggleNodePopout = () => {
  nodePopout.value = !nodePopout.value;
};

</script>

<template>
  <DialogPortal class="relative">
    <DialogOverlay @click.stop="emits('close')"
      class="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    />
 
      <DialogContent
        :class="cn(sheetVariants(), props.class)"
        v-bind="{ ...forwarded, ...$attrs }"
        tabindex="-1"
      >
        <transition-slide :offset="[128, 0]">
          <div v-if="nodePopout" class="p-6 z-[49] w-64 mt-12 h-max rounded-lg  bg-offwhite dark:bg-offmain border border-white dark:border-offmaindark">
            <div class="flex flex-col gap-1" @drop="drop" @dragover="allowDrop">
            <div class="flex gap-2 h-10 justify-between items-center">
              <button
                v-if="history.length > 0"
                @click="goBack"
                class="flex items-center justify-start gap-2 py-2 text-sm font-semibold group cursor-default"
              >
                <span class="fa-light fa-chevron-left"></span>
                <span class="group-hover:underline">Back</span>
              </button>
              <div v-else></div>
              <p>{{currPage.title}}</p>
            </div>
            <button
              v-for="(item, i) in currPage.items"
              :key="i"
              @click="item.submenu && item.submenu?.items.length > 0 ? navigateToSubmenu(item) : ''"
              :disabled="!item.submenu && !item.dataTransfer"
              :draggable="item.dataTransfer ? true : false"
              @dragstart="item.dataTransfer ? drag($event, item.dataTransfer) : null"
              @touchstart="item.dataTransfer ? touchStart($event, item.dataTransfer) : null"
              @touchend="item.dataTransfer ? touchEnd($event) : null"
              class="flex transform items-center justify-between rounded-lg px-2 py-2 text-sm font-semibold cursor-default transition-transform hover:underline"
              :class="{
                'cursor-grab bg-black/10 dark:bg-black/20 hover:scale-105 hover:no-underline hover:shadow-lg active:scale-100 active:cursor-grabbing':
                item.dataTransfer,
                'cursor-not-allowed text-opacity-50': !item.dataTransfer && !item.submenu
              }"
            >
              <span>{{ item.label }}</span>
              <span v-if="item.submenu && item.submenu.items?.length > 0" class="fa-light fa-chevron-right"></span>
            </button>
          </div>  
          </div>
        </transition-slide>
        <button @click="toggleNodePopout" v-if="!readonly && !disableVars" class="size-10 mt-12 z-[52] bg-offwhite group translate-x-[1px] rounded-tl-lg cursor-default rounded-bl-lg dark:bg-offmain border-l border-b border-t flex items-center justify-center border-white dark:border-offmaindark">
          <span class="size-6 fa-light fa-brackets-curly group-hover:text-green group-hover:bg-black/5  text-black dark:text-green bg-white rounded-full p-1" />
        </button>
        <div class="h-full bg-white dark:bg-offmaindark border-l p-6 border-white dark:border-offmaindark w-[90vw] max-w-2xl min-w-[400px,85vw] overflow-y-auto z-[51]">
          <slot /> 
        </div>
        <DialogClose @click.stop="emits('close')"
          class="absolute right-6 top-6 opacity-100 ring-offset-background transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-full disabled:pointer-events-none data-[state=open]:bg-secondary"
        >
          <X class="size-6 p-1 text-white bg-red-500 rounded-full" />
        </DialogClose>
      </DialogContent>
  </DialogPortal>
</template>
